import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  EmptyState,
  ErrorState,
  ETAPAS_COTACAO,
  Field,
  FormActions,
  FormSection,
  Input,
  PageHeader,
  ResumoValor,
  Skeleton,
  Stepper,
} from '../../../ui';
import ListaProdutos from '../../../components/produto/ListaProdutos';

import {
  fetchSgaProdutos,
  Produto,
  isProdutoCompulsorio,
  isProdutoPadrao,
} from '../../../api/sga-produto/sga-produtoApi';
import { gerarCotacaoPDF } from '../../../api/cotacao-pdf/cotacao-pdfApi';

import { clearCotacaoData, setCotacaoData } from '../../../store/cotacaoSlice';
import { RootState } from '../../../store/store';

import { extrairMensagemErro } from '../../../utils/apiError';
import { numeroParaMoeda, parseMoeda } from '../../../utils/moeda';
import { dadosDaCotacaoPendentes } from '../../../utils/cotacaoFluxo';

import { COTACAO } from '../../cotacao/routes';
import { HOME } from '../../home/routes';

/**
 * `/cotacao-produtos` — **etapa 2 (e última) do fluxo de cotação** (reescrita na
 * at-62, frente D/D1).
 *
 * O consultor escolhe os produtos do perfil, confirma o valor de adesão e gera o
 * **PDF da cotação**, que aparece aqui mesmo com o link para enviar ao cliente.
 * Não há tela terminal: o resultado é esta.
 *
 * ## O que a reescrita mudou de COMPORTAMENTO
 *
 *   1. 🔴 **O valor de adesão digitado com separador de milhar ia ao PDF 1000×
 *      menor.** O parse era `parseFloat(valor.replace(',', '.'))`: "1.350,00"
 *      virava `parseFloat("1.350.00")` = **1,35**, e era esse número que a tabela
 *      do documento imprimia como "Adesão: R$ 1,35" — num PDF **entregue ao
 *      associado**. É o mesmo defeito que a at-60 corrigiu em `/produto`; aqui
 *      sobreviveu porque a tela estava fora da frente C. Passou a usar o
 *      `parseMoeda` (at-30), o parser canônico do projeto.
 *   2. **Três estados que eram um só.** Sem cotação no fluxo (link direto, F5
 *      depois de limpar), erro de rede e "este perfil não tem produtos"
 *      mostravam **a mesma lista vazia** — e não havia indicação de carregamento.
 *      Agora são `EmptyState`, `ErrorState` com "Tentar novamente", `Skeleton` e
 *      um vazio explicado.
 *   3. **Campo de adesão vazio é recusado com mensagem.** Ele é alcançável — o
 *      campo **se limpa ao focar** — e antes mandava `NaN` ao backend. ⚠️ Assumir
 *      0 seria pior: zero é exatamente o valor que o item 52 combateu.
 *   4. **"Voltar" de verdade** no cabeçalho (era um botão solto "Cotações") e
 *      **Stepper** "Etapa 2 de 2".
 *   5. **"(obrigatório)" virou `Chip`** — era um `<span>` laranja escrito à mão.
 *   6. **O link do PDF ganhou um botão "Abrir PDF"**. A abertura automática
 *      continua, mas ela acontece **depois de um `await`** e por isso pode ser
 *      barrada por bloqueador de pop-up no celular; o botão é o caminho que
 *      sempre funciona.
 *
 * ## O que NÃO mudou, de propósito
 *
 * 🔴 **O payload de `POST /cotacao-pdf/gerar` é o mesmo, chave a chave** — o
 * `ValidationPipe` do backend usa `forbidNonWhitelisted` e um campo a mais
 * derruba a requisição inteira. Só o `adesao` mudou de parser.
 *
 * Também seguem: compulsórios pré-marcados e travados, `padrao` apenas ordenado
 * no topo (at-29), a composição do boleto médio (cota + rateio + produtos), o
 * nome **comercial** do produto no `produtos[]` (é por ele que o backend casa os
 * compulsórios) e o envio manual por WhatsApp — que **não é** o OctaDesk (at-40,
 * que serve só contratos).
 */
const CotacaoProdutoPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const voltar = useVoltar(COTACAO());

  const cotacao = useSelector((state: RootState) => state.cotacao);
  const {
    codigoRegionalSelecionada,
    codigoCooperativaSelecionada,
    tipoVeiculo,
    valorFipe,
  } = cotacao;

  /**
   * Sem cotação no fluxo não há perfil de onde tirar produtos. Acontece ao abrir
   * a rota direto, ao recarregar depois de o fluxo ter sido limpo, ou ao entrar
   * pelo menu (que despacha `clearCotacaoData`).
   */
  const semFluxo =
    !codigoRegionalSelecionada || !codigoCooperativaSelecionada || !tipoVeiculo || !valorFipe;

  /**
   * O que ficou faltando na etapa 1 — pinta a bolinha "Dados" de vermelho e
   * alimenta a faixa de aviso (at-62, pedido de Victor).
   *
   * ⚠️ **Avisa, não bloqueia**: o consultor pode tentar gerar e receber o erro
   * do backend. Uma regra local mais estrita que a do servidor barraria cotação
   * legítima — mesma escolha da checagem de CPF da at-58 (D3).
   */
  const pendencias = dadosDaCotacaoPendentes(cotacao);

  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [compulsorios, setCompulsorios] = useState<string[]>([]);

  const [valorAdesao, setValorAdesao] = useState('350');
  const [linkCotacao, setLinkCotacao] = useState<string | null>(null);

  /*
   * ⚠️ Só começa carregando quando há de fato o que buscar — com `true` fixo, a
   * tela sem fluxo ficaria num `Skeleton` **para sempre** (o efeito abaixo nem
   * chama a busca). É o mesmo cuidado que a at-60 tomou em `/produto`.
   */
  const [carregando, setCarregando] = useState(!semFluxo);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroValor, setErroValor] = useState<string | null>(null);

  const buscarProdutos = useCallback(async () => {
    if (semFluxo) return;

    setCarregando(true);
    setErro(null);

    try {
      const resposta = await fetchSgaProdutos(
        codigoRegionalSelecionada,
        codigoCooperativaSelecionada,
        tipoVeiculo,
        parseFloat(valorFipe),
      );

      const lista = resposta || [];

      // at-29: `padrao` só vai para o topo — NÃO vem pré-marcado (a flag do SGA é
      // destaque de catálogo, não contratação; o SGA Consultor não a soma).
      const obrigatorios = lista.filter(isProdutoCompulsorio);
      const padrao = lista.filter((p) => !isProdutoCompulsorio(p) && isProdutoPadrao(p));
      const demais = lista.filter((p) => !isProdutoCompulsorio(p) && !isProdutoPadrao(p));

      setProdutos([...obrigatorios, ...padrao, ...demais]);
      setCompulsorios(obrigatorios.map((p) => p.codigo_produto));
      setSelecionados(obrigatorios.map((p) => p.codigo_produto));
    } catch (err) {
      /*
       * Antes isto era um `console.error` e a lista ficava vazia — indistinguível
       * de "este perfil não tem produtos" (spec 02 §2.7: falha muda e spinner
       * eterno são o mesmo defeito).
       */
      setErro(extrairMensagemErro(err, 'Erro ao buscar os produtos deste perfil.'));
    } finally {
      setCarregando(false);
    }
  }, [
    semFluxo,
    codigoRegionalSelecionada,
    codigoCooperativaSelecionada,
    tipoVeiculo,
    valorFipe,
  ]);

  useEffect(() => {
    void buscarProdutos();
  }, [buscarProdutos]);

  const alternarProduto = (codigo: string) =>
    setSelecionados((antes) =>
      antes.includes(codigo) ? antes.filter((c) => c !== codigo) : [...antes, codigo],
    );

  const valorCota = Number(cotacao.valorCota || 0);
  const valorRateio = Number(cotacao.valorRateio || 0);

  const totalProdutos = selecionados.reduce((acumulado, codigo) => {
    const produto = produtos.find((p) => p.codigo_produto === codigo);
    return acumulado + parseFloat(produto?.valor_produto || '0');
  }, 0);

  /** Mesma composição de sempre — é o que vai no `mensalidade` do PDF. */
  const boletoMedio = valorCota + valorRateio + totalProdutos;

  const gerar = async () => {
    /*
     * ⚠️ `parseMoeda`, não `parseFloat(replace(',', '.'))`: aquele lia
     * "1.350,00" como 1,35 e imprimia R$ 1,35 no PDF do associado.
     */
    const adesaoNumero = parseMoeda(valorAdesao);

    if (!Number.isFinite(adesaoNumero)) {
      setErroValor('Informe o valor de adesão.');
      return;
    }
    setErroValor(null);

    setGerando(true);

    const escolhidos = produtos
      .filter((p) => selecionados.includes(p.codigo_produto))
      .map((p) => ({
        codigo_produto: p.codigo_produto,
        nome: p.descricao_produto,
        valor: parseFloat(p.valor_produto),
      }));

    dispatch(setCotacaoData({ produtos: escolhidos }));

    /*
     * 🔴 Payload IDÊNTICO ao de antes, chave a chave. `GerarCotacaoPdfDto` roda
     * sob `whitelist` + `forbidNonWhitelisted`: um campo a mais derruba a
     * requisição com 400, e um a menos muda o documento.
     */
    const dadosDoPdf = {
      numero: 0,
      nomeAssociado: cotacao.nome,
      nome: cotacao.nome,
      cpfAssociado: cotacao.cpf,
      placa: cotacao.placa,
      marca: cotacao.marca,
      modelo: cotacao.modelo,
      ano: Number(cotacao.ano),
      codigoFipe: cotacao.codigoFipe,
      valorFipe: cotacao.valorFipe,
      plano: cotacao.tipoVeiculoDescricao,
      codigoPlano: tipoVeiculo,
      produtos: escolhidos.map((p) => p.nome),
      produtosObrigatorios: escolhidos
        .filter((p) => compulsorios.includes(p.codigo_produto))
        .map((p) => p.nome),
      // perfil para a garantia de compulsórios no backend (at-04)
      codigoCooperativa: codigoCooperativaSelecionada,
      codigoTipoVeiculo: tipoVeiculo,
      mensalidade: boletoMedio.toFixed(2),
      adesao: adesaoNumero.toFixed(2),
      cotaParticipacao: cotacao.valorCotaParticipacao?.toFixed(2) || '0.00',
      porcentagemFipe: cotacao.porcentagemFipe?.toFixed(2) || '0.00',
    };

    try {
      const resposta = await gerarCotacaoPDF(dadosDoPdf);
      const url = resposta?.data?.url;

      if (!url) {
        toast.error('A cotação foi gerada, mas o link não veio na resposta.');
        return;
      }

      setLinkCotacao(url);
      // ⚠️ Pode ser barrado por bloqueador de pop-up (acontece depois do
      // `await`) — por isso o link fica na tela, com botão próprio.
      window.open(url, '_blank', 'noopener');
      toast.success('Cotação gerada');
    } catch (err) {
      toast.error(extrairMensagemErro(err, 'Erro inesperado ao gerar o PDF da cotação.'));
    } finally {
      setGerando(false);
    }
  };

  const enviarPorWhatsApp = () => {
    if (!cotacao.nome || !linkCotacao) {
      toast.warn('Nome do cliente ou link da cotação não encontrados.');
      return;
    }

    const mensagem = `Prezado(a) ${cotacao.nome}, Parabéns pela sua cotação! Estamos felizes em tê-lo conosco. Você pode acessar sua cotação através do seguinte link: ${linkCotacao}.`;

    window.open(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(mensagem)}`,
      '_blank',
      'noopener',
    );
  };

  return (
    <ShellSaac
      cabecalho={<PageHeader titulo="Produtos" sobretitulo="Cotação" aoVoltar={voltar} />}
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              {/*
                🔴 A bolinha "Dados" só fica verde se os dados estiverem mesmo
                completos (at-62). Antes ela ficava verde por estar atrás da
                etapa atual, e **afirmava** algo que ninguém tinha conferido.
              */}
              <Stepper
                etapa={2}
                rotulos={ETAPAS_COTACAO}
                pendentes={pendencias.length > 0 ? [1] : []}
                aoIrPara={(destino) => {
                  if (destino === 1) navigate(COTACAO());
                }}
              />
            </div>

            <div className="tw-flex tw-flex-col tw-gap-8 tw-p-4 md:tw-p-6">
              {semFluxo ? (
                <EmptyState
                  titulo="Nenhuma cotação em andamento"
                  descricao="Esta etapa é aberta depois de preencher os dados do cliente e do veículo."
                  icone="alerta"
                  acao={
                    <Botao variante="primario" onClick={() => navigate(COTACAO())}>
                      Ir para Cotação
                    </Botao>
                  }
                />
              ) : (
                <>
                  {/*
                    A bolinha vermelha diz QUE há pendência; esta faixa diz QUAL
                    — marcador de estado sem texto é o que a regra do projeto
                    proíbe (spec 02 §9). Vermelha, e não âmbar, pela mesma razão
                    da bolinha: aqui "pendente" significa que o documento não vai
                    sair, não que algo se resolve sozinho no cron.
                  */}
                  {pendencias.length > 0 && (
                    <div
                      className="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-border tw-border-danger/30 tw-bg-danger-bg tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                      role="alert"
                    >
                      <p className="tw-m-0 tw-text-sm tw-text-danger">
                        Faltam dados da etapa anterior: <strong>{pendencias.join(', ')}</strong>.
                      </p>
                      <Botao
                        variante="secundario"
                        tamanho="sm"
                        onClick={() => navigate(COTACAO())}
                        className="sm:tw-shrink-0"
                      >
                        Revisar dados
                      </Botao>
                    </div>
                  )}

                  <FormSection titulo="Valor de Adesão" icone="identificacao">
                    <Field
                      rotulo="Valor de adesão"
                      dica="Valor de entrada apresentado na cotação."
                      erro={erroValor ?? undefined}
                      campo="valorAdesao"
                    >
                      <Input
                        type="text"
                        inputMode="decimal"
                        autoComplete="off"
                        disabled={gerando}
                        value={valorAdesao}
                        /*
                         * O "350" é o padrão real (não um `placeholder`): quem
                         * não toca no campo cota com 350. Por isso ele sai ao
                         * focar, em vez de obrigar o consultor a apagá-lo.
                         */
                        onFocus={() => {
                          if (valorAdesao === '350') setValorAdesao('');
                        }}
                        onChange={(evento) => {
                          setValorAdesao(evento.target.value.replace(/[^0-9,.]/g, ''));
                          if (erroValor) setErroValor(null);
                        }}
                      />
                    </Field>
                  </FormSection>

                  <FormSection titulo="Produtos" icone="adesoes">
                    {carregando ? (
                      <Skeleton linhas={5} altura="tw-h-12" />
                    ) : erro ? (
                      <ErrorState
                        mensagem="Não foi possível carregar os produtos deste perfil."
                        detalhe={erro}
                        aoTentarNovamente={() => void buscarProdutos()}
                      />
                    ) : produtos.length === 0 ? (
                      <EmptyState
                        titulo="Nenhum produto disponível"
                        descricao="Não há produtos para a regional, a cooperativa e a faixa de valor deste veículo."
                        icone="sem-dados"
                      />
                    ) : (
                      <ListaProdutos
                        produtos={produtos}
                        selecionados={selecionados}
                        compulsorios={compulsorios}
                        desabilitado={gerando}
                        /* ⚠️ A descrição COMERCIAL, que é a que vai no
                           `produtos[]` do PDF (`/produto` mostra a do boleto). */
                        campoDescricao="descricao_produto"
                        aoAlternar={alternarProduto}
                      />
                    )}
                  </FormSection>

                  <ResumoValor
                    titulo="Boleto médio"
                    valor={numeroParaMoeda(boletoMedio)}
                    detalhe={
                      <>
                        cota {numeroParaMoeda(valorCota)} + rateio{' '}
                        {numeroParaMoeda(valorRateio)} + produtos{' '}
                        {numeroParaMoeda(totalProdutos)}
                      </>
                    }
                  />

                  {linkCotacao && (
                    <div className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-success/30 tw-bg-success-bg tw-p-4">
                      <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-success">
                        Cotação gerada.
                      </p>
                      <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
                        <Botao
                          variante="primario"
                          onClick={() => window.open(linkCotacao, '_blank', 'noopener')}
                        >
                          Abrir PDF
                        </Botao>
                        <Botao variante="secundario" onClick={enviarPorWhatsApp}>
                          Enviar por WhatsApp
                        </Botao>
                      </div>
                      <a
                        href={linkCotacao}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-break-all tw-text-xs tw-text-texto-suave tw-underline"
                      >
                        {linkCotacao}
                      </a>
                      {/*
                        ⚠️ Não é alarme, é informação: o PDF fica no GCS e a
                        `Cotacao` no banco, mas **não existe tela de consulta de
                        cotações geradas** (o subtítulo do menu promete "gerencie
                        as cotações" e a rota nunca foi criada). Sair daqui sem
                        enviar significa não reencontrar o link.
                      */}
                      <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                        Este link não volta a aparecer depois que você sair desta tela.
                      </p>
                    </div>
                  )}

                  {/*
                    at-62: esta é a tela TERMINAL do fluxo — não há `/concluido`
                    aqui. Depois de gerar, o consultor precisava do botão do
                    navegador ou do menu para sair (pedido de Victor,
                    10/09/2026), então a barra passa a oferecer a saída.

                    ⚠️ Antes de gerar, só o "Gerar cotação": oferecer saída para
                    quem ainda não terminou é convidar a abandonar o trabalho.
                  */}
                  <FormActions>
                    {linkCotacao ? (
                      <>
                        {/*
                          🔴 "Nova cotação" LIMPA o fluxo, como o menu e o
                          "Nova cotação" da carteira fazem (`limparCotacao`).
                          Sem isso a cotação seguinte herdaria esta — foi o
                          defeito que a at-38 corrigiu na adesão.
                        */}
                        <Botao
                          variante="primario"
                          tamanho="lg"
                          larguraTotal
                          className="sm:tw-w-auto"
                          onClick={() => {
                            dispatch(clearCotacaoData());
                            navigate(COTACAO());
                          }}
                        >
                          Nova cotação
                        </Botao>
                        {/* Continua disponível: uma cotação pode ser refeita com
                            outros produtos, até o limite de 4 por CPF/mês (mesma
                            lógica do "Gerar outro termo", at-60). */}
                        <Botao
                          variante="secundario"
                          tamanho="lg"
                          larguraTotal
                          carregando={gerando}
                          disabled={carregando || gerando || produtos.length === 0}
                          className="sm:tw-w-auto"
                          onClick={() => void gerar()}
                        >
                          {gerando ? 'Gerando...' : 'Gerar novamente'}
                        </Botao>
                        <Botao
                          variante="fantasma"
                          tamanho="lg"
                          larguraTotal
                          className="sm:tw-w-auto"
                          onClick={() => navigate(HOME())}
                        >
                          Início
                        </Botao>
                      </>
                    ) : (
                      <Botao
                        variante="primario"
                        tamanho="lg"
                        larguraTotal
                        carregando={gerando}
                        disabled={carregando || gerando || produtos.length === 0}
                        className="sm:tw-w-auto sm:tw-min-w-[220px]"
                        onClick={() => void gerar()}
                      >
                        {gerando ? 'Gerando...' : 'Gerar cotação'}
                      </Botao>
                    )}
                  </FormActions>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default CotacaoProdutoPage;
